import {
    createContext,
    Dispatch,
    MutableRefObject,
    ReactNode,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

const LEGACY_SOURCE = '__legacy';

type DirtyContextType = {
    isDirty: boolean;
    setIsDirty: Dispatch<SetStateAction<boolean>>;
    registerDirtySource: (key: string, dirty: boolean) => void;
    clearAllDirtySources: () => void;
    skipNextBlockRef: MutableRefObject<boolean>;
    skipNextBlock: () => void;
};

const DirtyContext = createContext<DirtyContextType | null>(null);

const useDirtyContext = () => {
    const dirtyContext = useContext(DirtyContext);

    if (!dirtyContext) {
        throw new Error("Use 'DirtyContext' inside it's provider");
    }

    return dirtyContext;
};

/**
 * Registers a named dirty source. Multiple sources can coexist; isDirty is the OR
 * of all currently-registered sources. The source is automatically cleared on
 * unmount and whenever `dirty` flips back to false.
 */
const useDirtySource = (key: string, dirty: boolean) => {
    const { registerDirtySource } = useDirtyContext();

    useEffect(() => {
        registerDirtySource(key, dirty);
    }, [key, dirty, registerDirtySource]);

    useEffect(
        () => () => {
            registerDirtySource(key, false);
        },
        [key, registerDirtySource],
    );
};

type DirtyContextProviderProps = {
    children: ReactNode;
};

const DirtyContextProvider = ({ children }: DirtyContextProviderProps) => {
    const [sources, setSources] = useState<Record<string, boolean>>({});
    // Held true for one navigation tick so blockAction can let the user's
    // already-confirmed navigation skip the global blocker dialog.
    const skipNextBlockRef = useRef(false);

    const isDirty = useMemo(() => Object.values(sources).some(Boolean), [sources]);

    const registerDirtySource = useCallback((key: string, dirty: boolean) => {
        setSources(prev => {
            const current = !!prev[key];
            if (current === dirty) return prev;
            if (!dirty) {
                const next = { ...prev };
                delete next[key];
                return next;
            }
            return { ...prev, [key]: true };
        });
    }, []);

    const setIsDirty = useCallback<Dispatch<SetStateAction<boolean>>>(value => {
        setSources(prev => {
            const current = !!prev[LEGACY_SOURCE];
            const nextValue = typeof value === 'function' ? value(current) : value;
            if (current === nextValue) return prev;
            if (!nextValue) {
                const nextSources = { ...prev };
                delete nextSources[LEGACY_SOURCE];
                return nextSources;
            }
            return { ...prev, [LEGACY_SOURCE]: true };
        });
    }, []);

    const clearAllDirtySources = useCallback(() => {
        setSources(prev => (Object.keys(prev).length === 0 ? prev : {}));
    }, []);

    useEffect(() => {
        if (!isDirty) return undefined;
        const handler = (event: BeforeUnloadEvent) => {
            event.preventDefault();
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [isDirty]);

    const skipNextBlock = useCallback(() => {
        skipNextBlockRef.current = true;
        // Release the flag after the current task so a stale skip can't leak
        // into an unrelated future navigation.
        setTimeout(() => {
            skipNextBlockRef.current = false;
        }, 0);
    }, []);

    const contextValue = useMemo(
        () => ({
            isDirty,
            setIsDirty,
            registerDirtySource,
            clearAllDirtySources,
            skipNextBlockRef,
            skipNextBlock,
        }),
        [isDirty, setIsDirty, registerDirtySource, clearAllDirtySources, skipNextBlock],
    );

    return <DirtyContext.Provider value={contextValue}>{children}</DirtyContext.Provider>;
};

export { DirtyContextProvider, useDirtyContext, useDirtySource };
